import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { Profile } from "@/hooks/useAuth";
import { useToast } from "@/components/feature/Toast";

function getDestination(profile: Profile): string {
  if (profile.role === "super_admin") return "/super-admin";
  if (profile.role === "admin") return "/admin/dashboard";
  if (profile.account_type === "seller") return "/seller/dashboard";
  return "/";
}

export default function LoginPage() {
  const { user, profile, loading, signIn, signOut, signInWithGoogle } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Si ya hay sesión activa, redirigimos según role + account_type.
  useEffect(() => {
    if (!loading && user && profile) {
      navigate(getDestination(profile), { replace: true });
    }
  }, [loading, user, profile, navigate]);

  const handleGoogleLogin = async () => {
    const { error: oauthError } = await signInWithGoogle();
    if (oauthError) {
      showToast(oauthError, "error");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError("Por favor, completa el correo y la contraseña.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await signIn(email.trim(), password);

      if (result.error) {
        setError(result.error);
        showToast(result.error, "error");
        return;
      }

      const signedProfile = result.profile;
      if (!signedProfile) {
        setError("No se pudo cargar tu perfil.");
        return;
      }

      // Cuenta suspendida/bloqueada
      if (!signedProfile.is_active) {
        await signOut();
        setError(
          "Tu cuenta ha sido suspendida. Contacta con soporte."
        );
        showToast("Tu cuenta ha sido suspendida. Contacta con soporte.", "error");
        return;
      }

      showToast("¡Bienvenido de nuevo!", "success");
      navigate(getDestination(signedProfile), { replace: true });
    } catch {
      setError("Ocurrió un error inesperado. Inténtalo de nuevo.");
      showToast("Ocurrió un error inesperado. Inténtalo de nuevo.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col lg:flex-row">
        {/* Panel de marca (solo escritorio) */}
        <div className="relative hidden overflow-hidden bg-primary-600 lg:block lg:w-1/2">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20marketplace%20illustration%20with%20floating%20product%20cards%2C%20shopping%20bags%2C%20and%20soft%20geometric%20shapes%20in%20warm%20amber%20and%20emerald%20accents%20over%20a%20deep%20indigo%20gradient%20background%2C%20clean%20editorial%20composition%2C%20subtle%20depth%20and%20soft%20lighting%2C%20premium%20elegant%20feel%20for%20a%20classifieds%20platform&width=1200&height=1400&seq=login-hero-01&orientation=portrait"
            alt="Ilustración del marketplace VendeYa"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-800/30 to-primary-700/60"></div>

          <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15 backdrop-blur-sm">
                <i className="ri-store-2-line text-xl text-white"></i>
              </span>
              <span className="font-heading text-xl font-bold">
                Vende<span className="text-accent-400">Ya</span>
              </span>
            </Link>

            <div className="space-y-6">
              <h2 className="font-heading text-3xl font-bold leading-tight">
                Compra y vende de forma simple, segura y visualmente agradable.
              </h2>
              <p className="max-w-md text-sm leading-relaxed text-white/85">
                Únete a miles de personas que ya publican sus productos nuevos y
                usados en la plataforma de clasificados más elegante de la región.
              </p>

              <div className="flex items-center gap-3 rounded-lg border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
                <i className="ri-shield-check-line text-2xl text-accent-400"></i>
                <p className="text-sm leading-snug text-white/90">
                  Tu cuenta está protegida con roles y permisos: comprador,
                  vendedor o administrador.
                </p>
              </div>
            </div>

            <p className="text-xs text-white/60">
              © {new Date().getFullYear()} VendeYa. Todos los derechos reservados.
            </p>
          </div>
        </div>

        {/* Formulario */}
        <div className="flex w-full items-center justify-center px-4 py-10 sm:px-6 lg:w-1/2 lg:px-14">
          <div className="w-full max-w-sm">
            {/* Logo móvil */}
            <div className="mb-8 flex flex-col items-center gap-3 lg:hidden">
              <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500">
                  <i className="ri-store-2-line text-xl text-white"></i>
                </span>
                <span className="font-heading text-xl font-bold text-foreground-950">
                  Vende<span className="text-primary-500">Ya</span>
                </span>
              </Link>
            </div>

            <div className="mb-8">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                Iniciar sesión
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                Ingresa tus datos para acceder a tu cuenta.
              </p>
            </div>

            {/* Google OAuth */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="mb-4 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-background-300 hover:bg-background-100"
            >
              <i className="ri-google-fill text-lg text-[#EA4335]"></i>
              Continuar con Google
            </button>

            <div className="mb-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-background-200"></span>
              <span className="text-xs font-medium text-foreground-500">o con tu correo</span>
              <span className="h-px flex-1 bg-background-200"></span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-100 px-3 py-2.5 text-sm text-accent-900"
                >
                  <i className="ri-error-warning-fill mt-0.5 text-accent-600"></i>
                  <span className="leading-snug">{error}</span>
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-sm font-medium text-foreground-700"
                >
                  Correo electrónico
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-foreground-400">
                    <i className="ri-mail-line text-base"></i>
                  </span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@correo.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-sm font-medium text-foreground-700"
                >
                  Contraseña
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-foreground-400">
                    <i className="ri-lock-line text-base"></i>
                  </span>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-11 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute inset-y-0 right-0 flex w-11 cursor-pointer items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
                  >
                    <i
                      className={`text-base ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`}
                    ></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded border-background-300 accent-primary-500"
                  />
                  Recuérdame
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary-500 transition-colors hover:text-primary-600"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-600 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting && (
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                )}
                {submitting ? "Iniciando sesión..." : "Iniciar sesión"}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-foreground-600">
              ¿No tienes una cuenta?{" "}
              <Link
                to="/registro"
                className="font-semibold text-primary-500 transition-colors hover:text-primary-600"
              >
                Regístrate gratis
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}